import { useState, type FormEvent } from 'react';
import { downloadFile } from '../../lib/api/download.js';
import { QaReportSchedules } from './QaReportSchedules.js';
import { useQuery } from '@tanstack/react-query';
import { toCsv, type QaReport, type QaReportGroup, type QaSearchResult } from '@arcnet/contracts';
import { api } from '../../lib/api/client.js';
import { formatDuration, formatNumber, relativeTime } from '../../lib/format.js';
import { Empty, ErrorBanner, Segmented, Stat, StatGroup, TableSkeleton } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';

/**
 * Auto QA reports and search (docs/37 §7, Q3).
 *
 * Reports are the marks, passes and sentiment of finished calls, by agent, department, queue or
 * campaign; which scorecard items fail most; and the trend by week. Every figure can be exported as
 * CSV. Search finds the calls where something was said -- the account's own users only.
 */

type Range = 'all' | '7' | '30' | '90';
const RANGES: ReadonlyArray<readonly [Range, string]> = [['7', '7 days'], ['30', '30 days'], ['90', '90 days'], ['all', 'All time']];
const GROUPS: ReadonlyArray<readonly [QaReportGroup, string]> = [['agent', 'Agent'], ['department', 'Department'], ['queue', 'Queue'], ['campaign', 'Campaign']];

const day = (d: Date): string => d.toLocaleDateString('en-CA');
function rangeParams(r: Range): URLSearchParams {
  const p = new URLSearchParams();
  if (r === 'all') return p;
  const to = new Date();
  const from = new Date(to);
  from.setDate(from.getDate() - (Number(r) - 1));
  p.set('from', day(from)); p.set('to', day(to));
  return p;
}
const pct = (n: number, of: number): string => (of ? `${Math.round((n / of) * 100)}%` : '—');
const rateTone = (n: number, of: number): 'ok' | 'warn' | 'danger' | undefined =>
  !of ? undefined : n / of >= 0.85 ? 'ok' : n / of >= 0.65 ? 'warn' : 'danger';
const scoreTone = (v: number | null): 'ok' | 'warn' | 'danger' | undefined =>
  v === null ? undefined : v >= 80 ? 'ok' : v >= 60 ? 'warn' : 'danger';
const sentimentTone = (v: number | null): 'ok' | 'warn' | 'danger' | undefined =>
  v === null ? undefined : v >= 0.25 ? 'ok' : v <= -0.25 ? 'danger' : 'warn';

/** Saves a CSV on the reader's machine. Nothing leaves the browser. */
function download(name: string, csv: string): void {
  const url = URL.createObjectURL(new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function Meter({ n, of, tone }: { n: number; of: number; tone: string | undefined }) {
  return (
    <span className="arc-meter" aria-hidden="true" style={{ width: 70 }}>
      <span style={{ width: `${of ? (n / of) * 100 : 0}%`, background: `var(--arc-${tone ?? 'ok'})` }} />
    </span>
  );
}

// ================================================================================== reports

export function QaReports({ tenantId }: { tenantId: string }) {
  const [range, setRange] = useState<Range>('30');
  const [group, setGroup] = useState<QaReportGroup>('agent');
  const p = rangeParams(range);
  p.set('group', group);
  const q = useQuery({
    queryKey: ['qa', tenantId, 'report', range, group],
    queryFn: () => api.get<QaReport>(`/v1/tenants/${tenantId}/qa/report?${p}`),
    placeholderData: (prev) => prev,
  });
  const r = q.data;
  const stamp = `${range === 'all' ? 'all-time' : `${range}-days`}-${day(new Date())}`;

  return (
    <>
      <div className="arc-row" style={{ gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <Segmented label="Range" value={range} onChange={setRange} options={RANGES} />
        <div className="arc-spacer" />
        {r && r.totals.calls > 0 && (
          <>
            <button type="button" className="arc-btn arc-btn-sm" onClick={() => exportReport(r, stamp)}>
              <Icon name="external" size={12} /> Export CSV
            </button>
            <button type="button" className="arc-btn arc-btn-sm"
              onClick={() => void downloadFile(`/v1/tenants/${tenantId}/qa/report/pdf?${p}`, `qa-report-by-${group}-${stamp}.pdf`)}>
              <Icon name="external" size={12} /> PDF
            </button>
          </>
        )}
      </div>
      {q.error && <ErrorBanner error={q.error} />}
      {!r ? <TableSkeleton rows={4} cols={4} /> : r.totals.calls === 0 ? (
        <div className="arc-card"><Empty icon="headset" title="No calls through QA in this range" hint="Calls appear here once their QA has finished." /></div>
      ) : (
        <>
          <StatGroup>
            <Stat bare icon="headset" hue={2} label="Calls" value={formatNumber(r.totals.calls)} sub={`${formatNumber(r.totals.marked)} marked`} />
            <Stat bare icon="check" hue={1} label="Average mark" value={r.totals.avg_score === null ? '—' : String(r.totals.avg_score)} tone={scoreTone(r.totals.avg_score)} sub="out of 100" />
            <Stat bare icon="activity" hue={3} label="Passed" value={pct(r.totals.passed, r.totals.marked)} tone={rateTone(r.totals.passed, r.totals.marked)}
              sub={`${formatNumber(r.totals.passed)} of ${formatNumber(r.totals.marked)}`} />
            <Stat bare icon="warning" hue={5} label="Auto-failed" value={formatNumber(r.totals.auto_failed)} tone={r.totals.auto_failed ? 'danger' : undefined}
              sub={`${formatNumber(r.totals.findings_high)} serious findings`} />
            <Stat bare icon="users" hue={4} label="Sentiment" value={r.totals.sentiment === null ? '—' : r.totals.sentiment.toFixed(2)} tone={sentimentTone(r.totals.sentiment)} sub="−1 to 1" />
          </StatGroup>

          <div className="arc-card" style={{ marginTop: 14 }}>
            <div className="arc-card-body arc-row" style={{ gap: 12, paddingBottom: 0, flexWrap: 'wrap' }}>
              <h3 className="arc-h2" style={{ margin: 0 }}>By</h3>
              <Segmented label="Group by" value={group} onChange={setGroup} options={GROUPS} />
            </div>
            <div className="arc-table-wrap">
              <table className="arc-table">
                <thead><tr><th>{GROUPS.find(([g]) => g === group)![1]}</th><th className="arc-td-right">Calls</th><th className="arc-td-right">Average mark</th>
                  <th className="arc-td-right">Passed</th><th className="arc-td-right">Auto-failed</th><th className="arc-td-right">Sentiment</th><th className="arc-td-right">Serious findings</th></tr></thead>
                <tbody>
                  {r.rows.map((x) => (
                    <tr key={x.key}>
                      <td>{x.name}</td>
                      <td className="arc-td-right arc-num">{formatNumber(x.calls)}</td>
                      <td className="arc-td-right arc-num" style={{ color: scoreTone(x.avg_score) ? `var(--arc-${scoreTone(x.avg_score)})` : undefined, fontWeight: 600 }}>{x.avg_score ?? '—'}</td>
                      <td className="arc-td-right arc-num">
                        <div className="arc-row" style={{ gap: 8, justifyContent: 'flex-end' }}>
                          <Meter n={x.passed} of={x.marked} tone={rateTone(x.passed, x.marked)} />{pct(x.passed, x.marked)}
                        </div>
                      </td>
                      <td className="arc-td-right arc-num" style={{ color: x.auto_failed ? 'var(--arc-danger)' : undefined }}>{formatNumber(x.auto_failed)}</td>
                      <td className="arc-td-right arc-num" style={{ color: sentimentTone(x.sentiment) ? `var(--arc-${sentimentTone(x.sentiment)})` : undefined }}>{x.sentiment?.toFixed(2) ?? '—'}</td>
                      <td className="arc-td-right arc-num">{formatNumber(x.findings_high)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 14, marginTop: 14 }}>
            <div className="arc-card arc-card-body">
              <h3 className="arc-h2" style={{ margin: '0 0 4px' }}>Scorecard items</h3>
              <p className="arc-hint" style={{ margin: '0 0 10px' }}>Weakest first.</p>
              {r.questions.length === 0 ? <p className="arc-subtle" style={{ margin: 0 }}>No calls were marked against a scorecard.</p> : (
                <div style={{ display: 'grid', gap: 9 }}>
                  {r.questions.map((x) => (
                    <div key={x.id}>
                      <div className="arc-row" style={{ gap: 8, fontSize: 13 }}>
                        <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: 2, flex: 'none', background: `var(--arc-${rateTone(x.passed, x.answered) ?? 'ok'})` }} />
                        <span>
                          {x.kind === 'phrase' ? <>Said: “{x.text}”</> : x.text}
                          <span className="arc-subtle" style={{ fontSize: 11.5 }}> · {x.scorecard}{x.auto_fail ? ' · fails the call' : ''}{x.overridden ? ` · ${x.overridden} changed by a supervisor` : ''}</span>
                        </span>
                        <div className="arc-spacer" />
                        <span className="arc-num">{formatNumber(x.passed)}/{formatNumber(x.answered)}</span>
                        <span className="arc-num arc-subtle" style={{ width: 40, textAlign: 'right' }}>{pct(x.passed, x.answered)}</span>
                      </div>
                      <span className="arc-meter" aria-hidden="true" style={{ marginTop: 3 }}>
                        <span style={{ width: `${x.answered ? (x.passed / x.answered) * 100 : 0}%`, background: `var(--arc-${rateTone(x.passed, x.answered) ?? 'ok'})` }} />
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <Weeks weeks={r.weeks} />
          </div>
        </>
      )}
      <QaReportSchedules tenantId={tenantId} />
    </>
  );
}

/** Average mark by week, with how many calls passed: what changed after coaching. */
function Weeks({ weeks }: { weeks: QaReport['weeks'] }) {
  return (
    <div className="arc-card arc-card-body">
      <h3 className="arc-h2" style={{ margin: '0 0 10px' }}>By week</h3>
      {weeks.length === 0 ? <p className="arc-subtle" style={{ margin: 0 }}>Nothing yet.</p> : (
        <>
          <div className="arc-row" style={{ gap: 6, alignItems: 'flex-end', height: 120 }} role="img"
            aria-label={weeks.map((w) => `Week of ${w.week}: average ${w.avg_score ?? 'none'}, ${w.passed} of ${w.marked} passed`).join('; ')}>
            {weeks.slice(-12).map((w) => (
              <div key={w.week} title={`Week of ${w.week}: average ${w.avg_score ?? '—'}, ${w.passed} of ${w.marked} passed, ${w.calls} calls`}
                style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'center', height: '100%', gap: 3 }}>
                <span className="arc-num" style={{ fontSize: 11, color: `var(--arc-${scoreTone(w.avg_score) ?? 'fg-3'})` }}>{w.avg_score === null ? '' : Math.round(w.avg_score)}</span>
                <div style={{ width: '100%', maxWidth: 38, height: `${w.avg_score ?? 0}%`, background: `var(--arc-${scoreTone(w.avg_score) ?? 'fg-4'})`, borderRadius: '3px 3px 0 0', opacity: 0.85 }} />
              </div>
            ))}
          </div>
          <div className="arc-row arc-subtle arc-num" style={{ gap: 6, fontSize: 10.5, marginTop: 4 }}>
            {weeks.slice(-12).map((w) => <span key={w.week} style={{ flex: 1, textAlign: 'center' }}>{w.week.slice(5)}</span>)}
          </div>
          <p className="arc-hint" style={{ margin: '8px 0 0' }}>Average mark per week (from Monday). Green at 80 and above, amber from 60.</p>
        </>
      )}
    </div>
  );
}

function exportReport(r: QaReport, stamp: string): void {
  const rows = r.rows.map((x) => [x.name, x.calls, x.marked, x.avg_score, x.passed, x.auto_failed, x.sentiment, x.findings_high]);
  const items = r.questions.map((x) => ['item', `${x.kind === 'phrase' ? 'Said: ' : ''}${x.text}`, x.scorecard, x.answered, x.passed, x.auto_fail ? 'yes' : 'no', x.overridden]);
  const weeks = r.weeks.map((w) => ['week', w.week, w.calls, w.marked, w.avg_score, w.passed, w.sentiment]);
  download(`qa-report-by-${r.group}-${stamp}.csv`,
    toCsv([r.group, 'calls', 'marked', 'average mark', 'passed', 'auto-failed', 'sentiment', 'serious findings'], rows)
    + '\r\n' + toCsv(['', 'scorecard item', 'scorecard', 'answered', 'passed', 'fails the call', 'changed by a supervisor'], items)
    + '\r\n' + toCsv(['', 'week from', 'calls', 'marked', 'average mark', 'passed', 'sentiment'], weeks));
}

// ================================================================================== search

export function QaSearch({ tenantId, onOpen }: { tenantId: string; onOpen: (runId: string) => void }) {
  const [text, setText] = useState('');
  const [speaker, setSpeaker] = useState<'either' | 'agent' | 'caller'>('either');
  const [range, setRange] = useState<Range>('90');
  const [asked, setAsked] = useState<string | null>(null);
  const p = rangeParams(range);
  if (asked) { p.set('q', asked); p.set('speaker', speaker); }
  const q = useQuery({
    queryKey: ['qa', tenantId, 'search', asked, speaker, range],
    queryFn: () => api.get<QaSearchResult>(`/v1/tenants/${tenantId}/qa/search?${p}`),
    enabled: !!asked,
    placeholderData: (prev) => prev,
  });
  const terms = (asked ?? '').toLowerCase().replace(/"/g, ' ').split(/\s+/).filter((t) => t.length > 1 && !t.startsWith('-'));

  const submit = (e: FormEvent) => { e.preventDefault(); if (text.trim().length >= 2) setAsked(text.trim()); };
  return (
    <>
      <form className="arc-card arc-card-body" onSubmit={submit} style={{ marginBottom: 14 }}>
        <div className="arc-row" style={{ gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="arc-field" style={{ margin: 0, flex: 1, minWidth: 240 }}>
            <label className="arc-label" htmlFor="qs-q">Find calls where someone said</label>
            <input id="qs-q" className="arc-input" value={text} maxLength={200} onChange={(e) => setText(e.target.value)}
              placeholder={'"cancel my account"'} autoComplete="off" />
          </div>
          <div className="arc-field" style={{ margin: 0 }}>
            <label className="arc-label" htmlFor="qs-s">Said by</label>
            <select id="qs-s" className="arc-input" value={speaker} onChange={(e) => setSpeaker(e.target.value as typeof speaker)}>
              <option value="either">Anyone</option><option value="agent">The agent</option><option value="caller">The caller</option>
            </select>
          </div>
          <Segmented label="Range" value={range} onChange={setRange} options={RANGES} />
          <button type="submit" className="arc-btn arc-btn-primary" disabled={text.trim().length < 2}><Icon name="search" size={13} /> Search</button>
        </div>
        <p className="arc-hint" style={{ margin: '8px 0 0' }}>Words match as said. "Quotes" for a phrase, -word to leave calls out.</p>
      </form>
      {q.error && <ErrorBanner error={q.error} />}
      {asked && q.data && (
        <div className="arc-card">
          <div className="arc-card-body arc-row" style={{ gap: 10, paddingBottom: 0 }}>
            <span className="arc-muted" style={{ fontSize: 12.5 }}>
              {q.data.hits.length === 0 ? 'No calls' : `${q.data.more ? 'The latest ' : ''}${q.data.hits.length} call${q.data.hits.length === 1 ? '' : 's'}`}
            </span>
            <div className="arc-spacer" />
            {q.data.hits.length > 0 && (
              <button type="button" className="arc-btn arc-btn-sm" onClick={() => download(`qa-search-${day(new Date())}.csv`, toCsv(
                ['when', 'extension', 'number', 'direction', 'mark', 'lines matched', 'first line', 'said by'],
                q.data!.hits.map((h) => [h.started_at, h.extension_number, h.party, h.direction, h.score, h.matches, h.lines[0]?.text, h.lines[0]?.speaker]),
              ))}><Icon name="external" size={12} /> Export CSV</button>
            )}
          </div>
          {q.data.hits.length === 0 ? <Empty icon="search" title="Nobody said that" hint="Only calls that have been through QA can be searched." /> : (
            <div style={{ padding: '6px 0' }}>
              {q.data.hits.map((h) => (
                <button key={h.run_id} type="button" className="arc-clickable" onClick={() => onOpen(h.run_id)}
                  style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', borderTop: '1px solid var(--arc-border)', padding: '10px 16px', cursor: 'pointer', color: 'inherit' }}>
                  <div className="arc-row" style={{ gap: 10, fontSize: 12.5, marginBottom: 4 }}>
                    <strong>{h.extension_number ?? '—'}</strong>
                    <span className="arc-mono arc-subtle">{h.party ?? ''}</span>
                    <span className="arc-subtle">{relativeTime(h.started_at)}</span>
                    <div className="arc-spacer" />
                    {h.matches > h.lines.length && <span className="arc-subtle">{h.matches} lines</span>}
                    {h.score !== null && <span className="arc-num" style={{ color: `var(--arc-${scoreTone(h.score) ?? 'fg'})`, fontWeight: 600 }}>{Math.round(h.score)}</span>}
                  </div>
                  {h.lines.map((l) => (
                    <div key={l.seq} className="arc-row" style={{ gap: 8, fontSize: 13, alignItems: 'baseline' }}>
                      <span className="arc-num arc-subtle" style={{ width: 44, flex: 'none' }}>{formatDuration(Math.floor(l.start_ms / 1000))}</span>
                      <span style={{ width: 52, flex: 'none', fontSize: 11.5, color: l.speaker === 'agent' ? 'var(--arc-c1)' : 'var(--arc-c4)' }}>{l.speaker === 'agent' ? 'Agent' : 'Caller'}</span>
                      <span><Highlight text={l.text} terms={terms} /></span>
                    </div>
                  ))}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}

/** The words searched for, marked in the line. Text only: the line is a transcript, not markup. */
function Highlight({ text, terms }: { text: string; terms: string[] }) {
  if (!terms.length) return <>{text}</>;
  const esc = terms.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const parts = text.split(new RegExp(`(\\b(?:${esc.join('|')})\\b)`, 'gi'));
  return <>{parts.map((p, i) => (i % 2 === 1
    ? <mark key={i} style={{ background: 'var(--arc-warn-bg)', color: 'inherit', borderRadius: 2, padding: '0 1px' }}>{p}</mark>
    : <span key={i}>{p}</span>))}</>;
}
